import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { decreaseFullScreenLoading, increaseFullScreenLoading } from "../../appSlice";
import { login, sessionRehydrated, userLoggedIn } from "./loginSlice";
import { Config } from "../../config";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import "./Login.scss";

// mode=add: reached via "Add another account" in the header - reuses this same form but must
// not redirect away just because another account is already logged in, and must not clear those
// other sessions on success (loginSlice.ts's login() reducer only ever appends/updates, it never
// clears other sessions).
// mode=reauth: reached by switching to an inactive session whose JWT has already expired -
// prefills that account's email so the user just has to re-enter their password. On success,
// login()'s user+domain matching replaces that session's stale token in place.
function Login() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const isAddingAccount = mode === "add" || mode === "reauth";

    const doLogin = async() => {
        dispatch(increaseFullScreenLoading());
        setLoginError(<></>);
        await fetch(Config.apiEndpoint + "/auth/login/", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*'
            },
            body: JSON.stringify({
                email: username,
                password: password
            })
        })
        .then((response) => {
            if (response.status === 401) {
                // Wrong password
                throw(new Error("LOGIN_FAILED"));
            } else if (response.status === 200) {
                return response.text()
            } else {
                throw(response);
            }
        })
        .then((token) => {
            dispatch(login(token));
            navigate("/inbox");
        })
        .catch((error) => {
            if (error.message === "LOGIN_FAILED") {
                // Display error
                setPassword("");
                setLoginError(<span className={"validation"}>Login error. Please double check email and password.</span>)
            }
        });
        dispatch(decreaseFullScreenLoading());
    }

    let [username, setUsername] = useState(mode === "reauth" ? (searchParams.get("email") ?? "") : "");
    let [password, setPassword] = useState('');
    let [loginError, setLoginError] = useState(<></>);

    let rehydrated = useAppSelector(sessionRehydrated);
    let loggedIn = useAppSelector(userLoggedIn);
    if (!rehydrated) {
        return null;
    }
    // Only auto-redirect away from a direct visit to /login while already logged in. When
    // adding/re-authenticating an account, another session being active is expected, and the
    // form itself navigates to /inbox once that account's login succeeds.
    if (loggedIn && !isAddingAccount) {
        return (<Navigate to="/inbox" />);
    }
    return(<div id={"loginContent"} className={"content"}>
        <h1>{mode === "add" ? "Add another account" : mode === "reauth" ? "Sign in again" : "Login"}</h1>
        <label>
            Please enter your company email address:
            <input type="text" placeholder={"email@companyname.com"} value={username} onChange={(e) => {setUsername(e.target.value)}} onKeyDown={(k) => {if (k.key === "Enter"){doLogin()}}} />
        </label>
        <label>
            Please enter your password:
            <input type="password" placeholder={"Password"} value={password} onChange={(e) => {setPassword(e.target.value)}} onKeyDown={(k) => {if (k.key === "Enter"){doLogin()}}} />
        </label>
        {loginError}
        <button className={"genericBtn"} onClick={doLogin}>Log In</button>
    </div>)
}

export default Login;
